var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { memo, useEffect, useState } from "react";
import styles from './HomePage.module.scss';
import Avatar from "../Avatar/Avatar";
import { useAppDispatch, useAppSelector } from "../../hooks/hook";
import { setChat } from "../../store/slices/setChatIDSlice";
import { profileAPI } from "../../API/api";
import classNames from "classnames";
import { makeChatId } from "../../utils/utils";
import { Badge } from "@mui/material";
import soundFile from '../../assets/sound.mp3';
import usePresenceStatus from "../../hooks/useCheckOnlineStatus";
import { setOnlineStatusSelectedUser } from "../../store/slices/appSlice";
import { useTypedTranslation } from "../../hooks/useTypedTranslation";
import CallIcon from '../../assets/telephone-fill.svg';
import DialogComponent, { NotFoundChat } from "../Settings/DialogComponent";
import { useChatPreview } from "../../hooks/useChatPreview";
const handleAudioPlay = () => {
    const audio = new Audio(soundFile);
    audio.play().catch(error => {
        console.error('Ошибка воспроизведения:', error);
    });
};
const Skeleton = () => {
    return (_jsxs("li", { className: classNames(styles.chatInfo, styles.skeleton), children: [_jsx("div", { className: styles.skeleton__Avatar }), _jsx("div", { className: styles.skeleton__Name })] }));
};
export const PreviewLastMessage = ({ message, currentUserId }) => {
    const { t } = useTypedTranslation();
    const isErrorColor = ((message === null || message === void 0 ? void 0 : message.callStatus) === 'rejected' || (message === null || message === void 0 ? void 0 : message.callStatus) === 'unanswered') && message.sender.uid !== currentUserId;
    const targetEl = () => {
        if (message === null || message === void 0 ? void 0 : message.shareChat)
            return (_jsx("div", { className: styles.lastMessage, style: { color: "#8774e1" }, children: _jsx("span", { children: t('contacts') }) }));
        if (!(message === null || message === void 0 ? void 0 : message.callStatus))
            return (_jsx("div", { className: styles.lastMessage, children: _jsx("span", { children: message.message }) }));
        return (_jsxs("div", { className: styles.lastMessage, style: { color: isErrorColor ? 'hsla(0, 73.92%, 60.75%, 0.75)' : 'auto', display: 'flex', gap: "6px", alignItems: 'center' }, children: [_jsx(CallIcon, {}), _jsx("span", { children: t(`call.${message.callStatus}`) })] }));
    };
    if (!message)
        return null;
    return (_jsx(_Fragment, { children: targetEl() }));
};
const ChatInfo = (user) => {
    const [updateUser, setUpdateUser] = useState(Object.assign({}, user));
    const [fetchingCurrentInfo, setFetchingCurrentInfo] = useState(true);
    const [notFoundUser, setNotFoundUser] = useState(false);
    const dispatch = useAppDispatch();
    const selectedChat = useAppSelector(state => state.app.selectedChat);
    const currentUser = useAppSelector(state => state.app.currentUser);
    const isContact = useAppSelector(state => state.app.contacts.find(c => c.uid === user.uid));
    const handleClick = () => {
        if ((selectedChat === null || selectedChat === void 0 ? void 0 : selectedChat.uid) === updateUser.uid)
            return;
        dispatch(setChat({ currentUser: currentUser, guestInfo: updateUser }));
    };
    const isSelected = (selectedChat === null || selectedChat === void 0 ? void 0 : selectedChat.uid) === user.uid;
    const presence = usePresenceStatus(updateUser.uid);
    const { lastMessage, unreadCount } = useChatPreview(user, currentUser.uid);
    const unsubscribe = () => {
        profileAPI.deletUserInMyChatlist({ myEmail: currentUser.email, deleteId: user.uid })
            .finally(() => {
            setNotFoundUser(false);
            dispatch(setChat(null));
        });
    };
    useEffect(() => {
        if (isSelected) {
            dispatch(setOnlineStatusSelectedUser(presence));
        }
    }, [presence, isSelected]);
    useEffect(() => {
        const getInfo = () => __awaiter(void 0, void 0, void 0, function* () {
            try {
                const currentInfo = yield profileAPI.getCurrentInfo(user.uid);
                if (!currentInfo)
                    throw currentInfo;
                const chatID = makeChatId({ currentUser, guestInfo: updateUser });
                if (currentInfo) {
                    if (chatID && (user.displayName !== currentInfo.displayName || user.photoURL !== currentInfo.photoURL)) {
                        yield profileAPI.updateUserInMyChatList(currentUser.email, currentInfo);
                    }
                    setUpdateUser(() => {
                        const user = Object.assign(Object.assign({}, currentInfo), { chatID });
                        if (isContact && (isContact === null || isContact === void 0 ? void 0 : isContact.nameWasGiven))
                            user.nameWasGiven = isContact.nameWasGiven;
                        return user;
                    });
                }
            }
            catch (error) {
                console.error('Error fetching current info:', error);
                setNotFoundUser(true);
            }
            finally {
                setFetchingCurrentInfo(false);
            }
        });
        getInfo();
    }, []);
    useEffect(() => {
        if (isSelected && !(updateUser === null || updateUser === void 0 ? void 0 : updateUser.chatID)) {
            setUpdateUser((prev) => (Object.assign(Object.assign({}, prev), { chatID: selectedChat.chatID })));
        }
    }, [selectedChat === null || selectedChat === void 0 ? void 0 : selectedChat.chatID]);
    if (fetchingCurrentInfo)
        return _jsx(Skeleton, {});
    if (notFoundUser)
        return (_jsx(DialogComponent, { isOpen: notFoundUser, onClose: unsubscribe, children: _jsx(NotFoundChat, { confirmFunc: unsubscribe, user: true }) }));
    return (_jsxs("li", { className: styles.chatInfo, onClick: handleClick, children: [isSelected &&
                _jsx("div", { className: styles.selected }), _jsx(Avatar, { url: updateUser === null || updateUser === void 0 ? void 0 : updateUser.photoURL, name: updateUser.displayName[0], isOnline: presence.isOnline }), _jsxs("div", { className: styles.nameBlock, children: [_jsx("div", { className: styles.name, children: _jsx("span", { className: styles.name, children: (updateUser === null || updateUser === void 0 ? void 0 : updateUser.nameWasGiven) || updateUser.displayName }) }), _jsx(PreviewLastMessage, { message: lastMessage, currentUserId: currentUser.uid })] }), _jsx("div", { className: styles.chatInfo__noRead, children: _jsx(Badge, { badgeContent: unreadCount, color: "primary" }) })] }));
};
function checkProps(prevProps, nextProps) {
    return prevProps.displayName === nextProps.displayName;
}
export default memo(ChatInfo, checkProps);
